import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import {
    Alert,
    Badge,
    Card,
    Col,
    Container,
    Row,
    Spinner,
} from 'react-bootstrap';
import {
    NavLink,
    useLocation,
    useNavigate,
} from 'react-router-dom';
import { FaEdit, FaEye } from 'react-icons/fa';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';

import API from '../../api/axios';
import SmartPagination from '../../components/Smart/SmartPagination';
import SmartTable from '../../components/Smart/SmartTable';
import SmartTopTool from '../../components/Smart/SmartTopTool';
import StatusAlert from '../../components/StatusAlert';
import { getRouteAlert } from '../../utils/alert';

const DEFAULT_PAGE_SIZE = 10;

const INITIAL_PAGE = {
    page_number: 1,
    total_pages: 1,
};

const INITIAL_META = {
    total: 0,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 1,
};

const INITIAL_EXACT_SEARCH = {
    companyName: '',
    country: '',
    currency: '',
};

const INITIAL_FILTERS = {
    status: [],
};

const EXACT_SEARCH_FIELDS = [
    {
        key: 'companyName',
        label: 'Company name',
        placeholder: 'Exact company name',
    },
    {
        key: 'country',
        label: 'Country',
        placeholder: 'Exact country name or ISO code',
    },
    {
        key: 'currency',
        label: 'Currency',
        placeholder: 'Exact currency code, e.g. EUR',
    },
];

const FILTER_OPTIONS = {
    status: ['active', 'inactive'],
};

const LANGUAGE_LABELS = {
    en: 'English',
    es: 'Spanish',
    pt: 'Portuguese',
    fr: 'French',
    it: 'Italian',
    de: 'German',
};

const getLanguageLabel = (language) => {
    const normalizedLanguage = String(language || '')
        .trim()
        .toLowerCase();

    if (!normalizedLanguage) {
        return '—';
    }

    return LANGUAGE_LABELS[normalizedLanguage] || language;
};

const getApiMeta = (responseData) => {
    const nestedMeta = responseData?.meta;

    return {
        total: Number(
            nestedMeta?.total ??
            responseData?.total ??
            0,
        ),
        page: Number(
            nestedMeta?.page ??
            responseData?.page ??
            1,
        ),
        pageSize: Number(
            nestedMeta?.pageSize ??
            responseData?.pageSize ??
            DEFAULT_PAGE_SIZE,
        ),
        totalPages: Math.max(
            1,
            Number(
                nestedMeta?.totalPages ??
                responseData?.totalPages ??
                1,
            ),
        ),
    };
};

const CompaniesList = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const [rows, setRows] = useState([]);
    const [meta, setMeta] = useState(INITIAL_META);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [pageAlert, setPageAlert] = useState(null);

    const [globalSearch, setGlobalSearch] = useState('');
    const [exactSearch, setExactSearch] = useState(
        INITIAL_EXACT_SEARCH,
    );
    const [filters, setFilters] = useState(
        INITIAL_FILTERS,
    );

    const [page, setPage] = useState(INITIAL_PAGE);

    const handleSetGlobalSearch = useCallback(
        (valueOrUpdater) => {
            setGlobalSearch((prev) =>
                typeof valueOrUpdater === 'function'
                    ? valueOrUpdater(prev)
                    : valueOrUpdater,
            );

            setPage((prev) =>
                prev.page_number === 1
                    ? prev
                    : {
                        ...prev,
                        page_number: 1,
                    },
            );
        },
        [],
    );

    const handleSetExactSearch = useCallback(
        (valueOrUpdater) => {
            setExactSearch((prev) =>
                typeof valueOrUpdater === 'function'
                    ? valueOrUpdater(prev)
                    : valueOrUpdater,
            );

            setPage((prev) =>
                prev.page_number === 1
                    ? prev
                    : {
                        ...prev,
                        page_number: 1,
                    },
            );
        },
        [],
    );

    const handleSetFilters = useCallback(
        (valueOrUpdater) => {
            setFilters((prev) =>
                typeof valueOrUpdater === 'function'
                    ? valueOrUpdater(prev)
                    : valueOrUpdater,
            );

            setPage((prev) =>
                prev.page_number === 1
                    ? prev
                    : {
                        ...prev,
                        page_number: 1,
                    },
            );
        },
        [],
    );

    const queryString = useMemo(() => {
        const params = new URLSearchParams();

        params.set(
            'page',
            String(
                Math.max(
                    1,
                    page.page_number || 1,
                ),
            ),
        );

        params.set(
            'pageSize',
            String(DEFAULT_PAGE_SIZE),
        );

        const normalizedGlobalSearch =
            globalSearch.trim();

        if (normalizedGlobalSearch) {
            params.set(
                'q',
                normalizedGlobalSearch,
            );
        }

        Object.entries(exactSearch).forEach(
            ([key, value]) => {
                const normalizedValue =
                    String(value ?? '').trim();

                if (normalizedValue) {
                    params.set(
                        key,
                        normalizedValue,
                    );
                }
            },
        );

        const selectedStatus =
            filters.status?.[0];

        if (selectedStatus) {
            params.set(
                'status',
                selectedStatus,
            );
        }

        return params.toString();
    }, [
        exactSearch,
        filters.status,
        globalSearch,
        page.page_number,
    ]);

    const fetchCompanies = useCallback(
        async (signal) => {
            try {
                setLoading(true);
                setError('');

                const { data } = await API.get(
                    `/supplier?${queryString}`,
                    {
                        signal,
                    },
                );

                const nextMeta =
                    getApiMeta(data);

                setRows(
                    Array.isArray(data?.data)
                        ? data.data
                        : [],
                );

                setMeta(nextMeta);

                setPage((prev) => {
                    const nextPage =
                        Math.min(
                            Math.max(
                                1,
                                nextMeta.page,
                            ),
                            nextMeta.totalPages,
                        );

                    if (
                        prev.page_number ===
                        nextPage &&
                        prev.total_pages ===
                        nextMeta.totalPages
                    ) {
                        return prev;
                    }

                    return {
                        page_number:
                            nextPage,
                        total_pages:
                            nextMeta.totalPages,
                    };
                });
            } catch (requestError) {
                if (
                    requestError?.code ===
                    'ERR_CANCELED' ||
                    requestError?.name ===
                    'CanceledError'
                ) {
                    return;
                }

                setRows([]);
                setMeta(INITIAL_META);

                setError(
                    requestError?.response?.data
                        ?.message ||
                    requestError?.message ||
                    'Failed to load companies.',
                );
            } finally {
                if (!signal.aborted) {
                    setLoading(false);
                }
            }
        },
        [queryString],
    );

    useEffect(() => {
        const controller =
            new AbortController();

        fetchCompanies(
            controller.signal,
        );

        return () => {
            controller.abort();
        };
    }, [fetchCompanies]);

    useEffect(() => {
        const routeAlert =
            getRouteAlert(location);

        if (!routeAlert) {
            return;
        }

        setPageAlert(routeAlert);

        navigate(
            location.pathname,
            {
                replace: true,
                state: {},
            },
        );
    }, [
        location,
        navigate,
    ]);

    const columns = useMemo(
        () => [
            {
                keys: [
                    'id',
                    'companyName',
                ],
                label: 'Company Name',
                render: (item) => (
                    <div className="d-flex align-items-center">
                        <OverlayTrigger
                            placement="top"
                            overlay={
                                <Tooltip
                                    id={`supplier-${item.id}-tooltip`}
                                >
                                    ID: {item.id}
                                </Tooltip>
                            }
                        >
                            <span
                                className="text-break"
                                style={{
                                    cursor: 'help',
                                }}
                            >
                                {item.companyName ||
                                    '—'}
                            </span>
                        </OverlayTrigger>
                    </div>
                ),
            },
            {
                key: 'country',
                label: 'Country',
                render: (item) => (
                    <span
                        title={
                            item.countryCode ||
                            undefined
                        }
                    >
                        {item.country ||
                            '—'}
                    </span>
                ),
            },
            {
                keys: [
                    'currency',
                    'language',
                ],
                label:
                    'Currency & Language',
                render: (item) => (
                    <span>
                        {item.currency ||
                            '—'}{' '}
                        ·{' '}
                        {getLanguageLabel(
                            item.language,
                        )}
                    </span>
                ),
            },
            {
                key: 'isActive',
                label: 'Status',
                render: (item) => (
                    <Badge
                        bg={
                            item.isActive
                                ? 'success'
                                : 'danger'
                        }
                    >
                        {item.isActive
                            ? 'Active'
                            : 'Inactive'}
                    </Badge>
                ),
            },
            {
                key: 'actions',
                label: 'Actions',
                render: (item) => (
                    <div className="d-flex gap-2 align-items-center">
                        <NavLink
                            to={`/company/edit/${item.id}`}
                            className="btn btn-view p-0"
                            title="View"
                            aria-label={`View ${item.companyName ||
                                'company'
                                }`}
                        >
                            <FaEye
                                size={20}
                            />
                        </NavLink>

                        <NavLink
                            to={`/company/edit/${item.id}`}
                            className="btn btn-edit p-0"
                            title="Edit"
                            aria-label={`Edit ${item.companyName ||
                                'company'
                                }`}
                        >
                            <FaEdit
                                size={20}
                            />
                        </NavLink>
                    </div>
                ),
            },
        ],
        [],
    );

    return (
        <Container
            fluid
            className="d-flex flex-column"
        >
            {pageAlert && (
                <Row className="mb-3">
                    <Col sm={12}>
                        <StatusAlert
                            alert={pageAlert}
                            onClose={() =>
                                setPageAlert(
                                    null,
                                )
                            }
                        />
                    </Col>
                </Row>
            )}

            <Row className="mb-4">
                <Col sm={8}>
                    <span className="fw-semibold">
                        Companies List
                    </span>
                </Col>

                <Col
                    sm={4}
                    className="text-end"
                >
                    <span className="fw-semibold">
                        <NavLink
                            to="/dashboard"
                            className="block-link"
                        >
                            Dashboard
                        </NavLink>

                        {' - Companies List'}
                    </span>
                </Col>
            </Row>

            <Row>
                <Col sm={12}>
                    <Card className="h-100 p-2">
                        <Card.Body className="px-2 pt-2 pb-5">
                            <Row>
                                <Col md={12}>
                                    <SmartTopTool
                                        advancedSearch
                                        globalSearch={
                                            globalSearch
                                        }
                                        setGlobalSearch={
                                            handleSetGlobalSearch
                                        }
                                        globalSearchPlaceholder="Search company name or country..."
                                        exactSearch={
                                            exactSearch
                                        }
                                        setExactSearch={
                                            handleSetExactSearch
                                        }
                                        exactSearchFields={
                                            EXACT_SEARCH_FIELDS
                                        }
                                        filterOptions={
                                            FILTER_OPTIONS
                                        }
                                        filters={
                                            filters
                                        }
                                        setFilters={
                                            handleSetFilters
                                        }
                                        debounceMs={
                                            400
                                        }
                                    />
                                </Col>

                                <Col md={12}>
                                    {error && (
                                        <Alert
                                            variant="danger"
                                            className="mb-3"
                                        >
                                            {
                                                error
                                            }
                                        </Alert>
                                    )}

                                    {loading &&
                                        rows.length ===
                                        0 ? (
                                        <div className="d-flex justify-content-center align-items-center py-5">
                                            <Spinner
                                                animation="border"
                                                role="status"
                                                aria-label="Loading companies"
                                            />
                                        </div>
                                    ) : (
                                        <SmartTable
                                            data={
                                                rows
                                            }
                                            columns={
                                                columns
                                            }
                                            emptyState={
                                                <div className="text-center py-5 text-muted">
                                                    <p className="mb-0">
                                                        No
                                                        matching
                                                        companies
                                                        found
                                                    </p>
                                                </div>
                                            }
                                        />
                                    )}
                                </Col>

                                <Col
                                    md={12}
                                    className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3"
                                >
                                    <small className="text-muted">
                                        {
                                            meta.total
                                        }{' '}
                                        compan
                                        {meta.total ===
                                            1
                                            ? 'y'
                                            : 'ies'}
                                    </small>

                                    <SmartPagination
                                        page={
                                            page
                                        }
                                        setPage={
                                            setPage
                                        }
                                    />
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default CompaniesList;